import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { PolicyService } from '@services/policy.service';
import { LoadingComponent } from '@components/shared';
import {
  PolicyDetail,
  PolicyFamily,
  PolicyItemColumn,
  POLICY_FAMILIES,
  POLICY_ITEM_COLUMNS
} from '@models/policy.model';

@Component({
  selector: 'app-policy-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, NgbNavModule, LoadingComponent],
  templateUrl: './policy-detail.component.html',
  styleUrl: './policy-detail.component.scss'
})
export class PolicyDetailComponent implements OnInit, OnDestroy {
  family: PolicyFamily = 'payment';
  policyId = '';
  policy: PolicyDetail | null = null;
  columns: PolicyItemColumn[] = [];
  loading = false;
  errorMessage: string | null = null;

  activeTab = 'published';

  private subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private policyService: PolicyService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.route.paramMap.subscribe(params => {
        this.family = (params.get('family') as PolicyFamily) ?? 'payment';
        this.policyId = params.get('policyId') ?? '';
        this.columns = POLICY_ITEM_COLUMNS[this.family];
        this.load();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  familyLabel(): string {
    return POLICY_FAMILIES.find(f => f.key === this.family)?.label ?? this.family;
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'text-bg-success';
      case 'inactive':
        return 'text-bg-secondary';
      case 'draftOnly':
        return 'text-bg-warning';
      default:
        return 'text-bg-light';
    }
  }

  /**
   * Dynamic field access for the generic items table — the trade-off that comes with not having
   * a /policy-schemas/{family} endpoint yet to describe these fields properly (see policy.model.ts).
   */
  cellValue(item: any, column: PolicyItemColumn): any {
    return item[column.key];
  }

  /**
   * Renders a payment-type-filter-style array: "*" means it applies to every payment type.
   */
  formatArrayValue(value: unknown): string {
    if (!Array.isArray(value) || value.length === 0) {
      return '—';
    }
    if (value.includes('*')) {
      return 'All';
    }
    return value.join(', ');
  }

  retry(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.policy = null;

    const versionParam = this.route.snapshot.queryParamMap.get('version');
    const version = versionParam ? Number(versionParam) : undefined;

    this.policyService.getPolicy(this.family, this.policyId, version).subscribe({
      next: policy => {
        this.policy = policy;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err: Error) => {
        this.errorMessage = err.message;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }
}
